import React, { useEffect, useMemo, useRef } from 'react';
import { useParams, useLocation, useNavigate, Outlet, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANG_CODES, DEFAULT_LANGUAGE } from 'i18n/config';
import { useAppSelector } from 'store/hooks';
import { SuspenseLoader } from 'components/loader/loader';
import { getLocalizedPath, getUnlocalizedPath } from 'utils/localizedRouting';

export const getSavedLanguage = (): string => {
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem('language');
    if (saved && (SUPPORTED_LANG_CODES as readonly string[]).includes(saved)) {
      return saved;
    }
  }
  return DEFAULT_LANGUAGE;
};

export interface LanguageRouteWrapperProps {
  isRoot?: boolean;
}

/**
 * Validates and synchronizes routing with i18next and user preferences.
 * Implements the "prefix-except-default" routing model:
 * 1. Root / clean URLs (isRoot = true):
 *    - Default language ('en') has NO URL prefix (e.g. '/', '/casino', '/affiliate').
 *    - Authenticated users with a supported non-default language preference (e.g. 'ru')
 *      are automatically redirected to their localized URL (e.g. '/ru/casino').
 * 2. Localized URLs (/:lang):
 *    - If lang is DEFAULT_LANGUAGE ('en'), automatically redirects to the clean URL (e.g. '/en/casino' -> '/casino').
 *    - If lang is an unsupported language slug (e.g. '/ru/casino' while translations are locked or disabled by backend),
 *      redirects to the clean default URL ('/casino').
 *    - If valid non-default language (e.g. '/ru' when enabled), synchronizes i18n and renders.
 */
export const LanguageRouteWrapper: React.FC<LanguageRouteWrapperProps> = ({ isRoot = false }) => {
  const { lang } = useParams<{ lang?: string }>();
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, isInitializing } = useAppSelector((state) => state.auth);
  const { languages: siteLanguages, hasFetchedLanguages } = useAppSelector((state) => state.site);

  // Determine active supported languages:
  // When backend languages are loaded, valid languages are the intersection of backend enabled languages
  // and frontend supported language bundles (SUPPORTED_LANG_CODES).
  // Before backend responds, fallback to SUPPORTED_LANG_CODES.
  const activeLanguages: readonly string[] = useMemo(() => {
    if (hasFetchedLanguages && siteLanguages && siteLanguages.length > 0) {
      const backendCodes = siteLanguages.map((l) => l.code);
      return (SUPPORTED_LANG_CODES as readonly string[]).filter((code) =>
        backendCodes.includes(code),
      );
    }
    return SUPPORTED_LANG_CODES;
  }, [hasFetchedLanguages, siteLanguages]);

  const isValidLang = Boolean(lang && activeLanguages.includes(lang));
  const userLang = user?.language || user?.profile?.language;
  const isSupportedUserLang = Boolean(userLang && activeLanguages.includes(userLang));

  // If session is still initializing on page load/refresh, hold briefly to avoid flash of wrong language
  const isInitialAuthChecking = isInitializing;

  // If visiting a localized route (/:lang, e.g. /ru/casino), hold briefly until backend languages are fetched
  // so we don't flash a disabled language before redirecting to clean default route
  const isWaitingForLanguages = !isRoot && !hasFetchedLanguages;

  useEffect(() => {
    const targetLang = isRoot ? DEFAULT_LANGUAGE : isValidLang && lang ? lang : DEFAULT_LANGUAGE;

    if (typeof document !== 'undefined') {
      document.documentElement.lang = targetLang;
    }

    if (isRoot) {
      if (i18n.language !== DEFAULT_LANGUAGE) {
        i18n.changeLanguage(DEFAULT_LANGUAGE);
      }
      localStorage.setItem('language', DEFAULT_LANGUAGE);
    } else if (isValidLang && lang && lang !== DEFAULT_LANGUAGE) {
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang);
      }
      localStorage.setItem('language', lang);
    }
  }, [isRoot, lang, isValidLang, i18n]);

  // Compute the unlocalized path cleanly (strip leading language segment if present)
  const restOfPath = location.pathname.replace(/^\/[^/]+/, '');
  const unlocalizedPath = (restOfPath ? restOfPath : '/') + location.search + location.hash;

  // Initial entry redirect for authenticated users who land on a route different from their preferred language
  const hasRedirectedRef = useRef(false);

  useEffect(() => {
    if (
      !hasRedirectedRef.current &&
      hasFetchedLanguages &&
      isAuthenticated &&
      isSupportedUserLang &&
      userLang
    ) {
      if (isRoot && userLang !== DEFAULT_LANGUAGE) {
        hasRedirectedRef.current = true;
        const targetPath =
          getLocalizedPath(location.pathname, userLang) + location.search + location.hash;
        navigate(targetPath, { replace: true, state: location.state });
      } else if (!isRoot && isValidLang && lang && lang !== userLang) {
        hasRedirectedRef.current = true;
        const targetPath = getLocalizedPath(unlocalizedPath, userLang);
        navigate(targetPath, { replace: true, state: location.state });
      }
    }
  }, [
    isRoot,
    hasFetchedLanguages,
    isAuthenticated,
    isSupportedUserLang,
    userLang,
    isValidLang,
    lang,
    location.pathname,
    location.search,
    location.hash,
    location.state,
    unlocalizedPath,
    navigate,
  ]);

  if (isInitialAuthChecking || isWaitingForLanguages) {
    return <SuspenseLoader />;
  }

  // --- 1. Root route handling (clean URLs, e.g. /, /casino, /affiliate) ---
  if (isRoot) {
    return <Outlet />;
  }

  // --- 2. Localized route handling under /:lang ---
  // A. If lang is the default language (e.g. /en or /en/casino),
  // strip /en and redirect to clean unlocalized path (e.g. / or /casino)
  if (lang === DEFAULT_LANGUAGE) {
    return <Navigate to={unlocalizedPath} replace state={location.state} />;
  }

  // B. Check if lang slug matches language pattern (e.g. 2-3 letters: 'kz', 'ru', 'tr', 'es', 'pt-br')
  const isLanguageSlug = Boolean(lang && /^[a-z]{2,3}(-[a-z]{2})?$/i.test(lang));

  // If it's a language code that is NOT currently supported (e.g. /kz or /kz/casino):
  // Redirect to user's preferred supported language or default clean URL
  if (isLanguageSlug && !isValidLang) {
    const targetLang =
      isAuthenticated && isSupportedUserLang && userLang && userLang !== DEFAULT_LANGUAGE
        ? userLang
        : DEFAULT_LANGUAGE;

    const targetPath = getLocalizedPath(unlocalizedPath, targetLang);
    return <Navigate to={targetPath} replace state={location.state} />;
  }

  // If it's NOT a language slug (e.g. /some-broken-path reached /:lang), let it render Outlet
  // so that the child wildcard route can render NotFound
  if (!isLanguageSlug) {
    return <Outlet />;
  }

  // C. Valid non-default language (e.g. /ru when Russian is enabled)
  return <Outlet />;
};

/**
 * Fallback redirect for legacy or missing routes.
 */
export const LegacyRouteRedirect: React.FC = () => {
  const location = useLocation();
  const cleanPath = getUnlocalizedPath(location.pathname) + location.search + location.hash;
  return <Navigate to={cleanPath} replace state={location.state} />;
};
